// @ts-nocheck
import React, { useState, useEffect } from 'react';
import ActionBar from '../layout/ActionBar';
import AutoSizer from 'react-virtualized/dist/commonjs/AutoSizer';
import MultiGrid from 'react-virtualized/dist/commonjs/MultiGrid';
import KwPanel from '../common/KwPanel';
import {
  cellRenderer,
  getColumnWidth,
  getRowHeight,
} from './components/Bulk/utils';
import { hasPermission } from '../../components/auth/authUtils';
import Search from './components/Bulk/Search';
import { toast } from 'react-toastify';
import permissionTypes from '../../constants/permissionTypes';
import { getRequestableCompanies } from '../../actions/companyActions';
import {
  getRequestableKeyWorkflows,
  getGamesForWorkflows,
} from '../../actions/workflowActions';
import { CommaNumber_NoDecimal } from '../../util/formatter';
import Loading from '../common/Loading';
import SelectCompanyStep from './slideout/wizard/SelectCompanyStep';
import SelectKeyWorkflowStep from './slideout/wizard/SelectKeyWorkflowStep';
import { getKeyProviders as fetchKeyProviders } from '../../actions/keyProviderActions';
import { getPlatforms as fetchPlatforms } from '../../actions/platformActions';
import { getAllTerritories as fetchTerritories } from '../../actions/territoryActions';
import Button from '../common/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import icons from '../../constants/icons';
import { usePageTitle } from '../../util/pageTitle';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { bulkSaveRequests } from '../../reducers/basketReducer';

export interface BulkProps {
  setClasses: (classes: string) => void;
  setCompleteRequestSlideoutOpen: (open: boolean) => void;
}

/** Row in the bulk grid: either a game header or an SKU row */
export interface BulkGameRow {
  gameId?: string;
  skuId?: string;
  name?: string;
  isSKU?: boolean;
  numberOfKeys?: number | string;
  tag?: string;
  showRemove?: boolean;
  gameName?: string;
  packshotUrl?: string;
  appId?: string;
  skuNames?: string[];
  packageIds?: string[];
  keyProviders?: string[];
  platforms?: string[];
  territories?: Array<{ id: string; name?: string } | undefined>;
  packageType?: string;
  packageId?: string;
  platformId?: string;
  platform?: string;
  platformImageId?: string;
  platformColourHex?: string;
  keyProviderId?: string;
  keyProviderName?: string;
  keyProviderImageId?: string;
  keyProviderColourHex?: string;
  territoriesRestricted?: boolean;
  [key: string]: unknown;
}

/** Game returned from getGamesForWorkflows with skUs */
interface GameWithSkUs {
  id: string;
  name?: string;
  packshotUrl?: string;
  appId?: string;
  skUs?: Array<{
    skuId: string;
    name?: string;
    packageId?: string;
    packageType?: string;
    platformId?: string;
    platform?: string;
    platformImageId?: string;
    platformColourHex?: string;
    keyProviderId?: string;
    keyProviderName?: string;
    keyProviderImageId?: string;
    keyProviderColourHex?: string;
    territories?: string[];
    territoriesRestricted?: boolean;
  }>;
}

function Bulk({ setClasses, setCompleteRequestSlideoutOpen }: BulkProps) {
  usePageTitle(['Bulk request']);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => state.auth);
  const keyProviders = useAppSelector((state) => state.keyProviders);
  const platforms = useAppSelector((state) => state.platforms);
  const territories = useAppSelector((state) => state.territories);
  const getKeyProviders = () => dispatch(fetchKeyProviders());
  const getPlatforms = () => dispatch(fetchPlatforms());
  const getAllTerritories = () => dispatch(fetchTerritories());

  const [breadcrumb, setBreadcrumb] = useState<Array<{ label: string; link?: string }> | undefined>();
  const [showRemove, setShowRemove] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [companyId, setCompanyId] = useState<string | undefined>();
  const [companies, setCompanies] = useState<Array<{ id: string; companyName?: string }> | undefined>();
  const [company, setCompany] = useState<{ id: string; companyName?: string; name?: string } | undefined>();
  const requestOnBehalfOf = hasPermission(
    currentUser,
    permissionTypes.REQUEST_ON_BEHALF_OF
  );
  useEffect(() => {
    if (!companies && requestOnBehalfOf) {
      getRequestableCompanies()
        .then((_companies) => {
          setCompanies(_companies);
          if (_companies.length === 1) {
            setCompanyId(_companies[0].id);
          } else {
            setIsLoading(false);
          }
        })
        .catch((e) => {
          toast.error(e.message || t('Failed to get list of companies'));
          setIsLoading(false);
        });
    } else if (companies && companies.length === 1) {
      setCompanyId(companies[0]!.id);
    } else if (!companies && currentUser?.companyId) {
      setCompanies([{ id: currentUser.companyId }]);
      setCompanyId(currentUser.companyId);
    }
  }, [companies, requestOnBehalfOf, currentUser, t]);

  useEffect(() => {
    if (companies && companyId && !company) {
      setCompany(companies.find((x) => x.id === companyId));
    }
  }, [companyId, companies, company]);
  const [keyWorkflowId, setKeyWorkflowId] = useState<string | undefined>();
  const [keyWorkflow, setKeyWorkflow] = useState<{ id: string; name?: string; tagsAsFreeText?: boolean } | undefined>();
  const [keyWorkflows, setKeyWorkflows] = useState<Array<{ id: string; name?: string }> | undefined>();

  //Get the list of key workflows
  useEffect(() => {
    if (companyId) {
      getRequestableKeyWorkflows(requestOnBehalfOf ? companyId : '')
        .then((workflows) => {
          setKeyWorkflows(workflows);
          if (workflows.length === 1) {
            setKeyWorkflowId(workflows[0]!.id);
          } else {
            setIsLoading(false);
          }
        })
        .catch((e) => {
          toast.error(e.message || t('Failed to get list of workflows'));
          setIsLoading(false);
        });
    }
  }, [companyId, companies, requestOnBehalfOf, t]);

  useEffect(() => {
    if (keyWorkflows && keyWorkflowId && !keyWorkflow) {
      setKeyWorkflow(keyWorkflows.find((x) => x.id === keyWorkflowId));
    }
  }, [keyWorkflowId, keyWorkflows, keyWorkflow]);

  useEffect(() => {
    if (keyWorkflow && company) {
      setBreadcrumb([
        { label: t('Bulk requests'), link: '/requests/bulk' },
        { label: company.companyName ?? company.name ?? '' },
        { label: keyWorkflow.name ?? '' },
      ]);
    } else if (company && !keyWorkflow) {
      setBreadcrumb([
        { label: t('Bulk requests'), link: '/requests/bulk' },
        { label: company.companyName ?? company.name ?? '' },
      ]);
    } else {
      setBreadcrumb([{ label: t('Bulk requests'), link: '/requests/bulk' }]);
    }
  }, [keyWorkflow, company, t]);

  const [games, setGames] = useState<BulkGameRow[]>([]);

  const toggleShowRemove = (_id?: string) => {
    setGames([...games.map((game) => ({ ...game, showRemove: !showRemove }))]);
    setShowRemove(!showRemove);
  };

  const rowRenderer = ({ style }: { style: React.CSSProperties }) => {
    return <div style={style}></div>;
  };

  const handleRemove = ({ gameId, skuId }: { gameId: string; skuId?: string }) => {
    if (skuId) {
      const updatedGames = [...games.filter((game) => game.skuId !== skuId)];
      setGames(updatedGames);
      if (
        updatedGames.filter((game) => game.gameId === gameId && game.isSKU)
          .length === 0
      ) {
        setGames([...games.filter((game) => game.gameId !== gameId)]);
      }
    } else {
      setGames([...games.filter((game) => game.gameId !== gameId)]);
    }
  };

  const [bulkNumberOfKeys, setBulkNumberOfKeys] = useState<string | undefined>();
  const bulkNumberOfKeysOnBlur = () => {
    setBulkNumberOfKeys(undefined);
  };

  const bulkChangeNumberOfKeys = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBulkNumberOfKeys(e.target.value);
    setGames([
      ...games.map((game) =>
        game.isSKU && filteredGames.some((x) => x.skuId === game.skuId)
          ? { ...game, numberOfKeys: e.target.value }
          : game
      ),
    ]);
  };

  const changeNumberOfKeys = (numberOfKeys: number | string, game: BulkGameRow) => {
    game.skuId
      ? setGames([
        ...games.map((x) =>
          x.skuId === game.skuId ? { ...x, numberOfKeys } : x
        ),
      ])
      : setGames([
        ...games.map((x) =>
          x.gameId === game.gameId &&
            filteredGames.some((g) => g.skuId === x.skuId)
            ? { ...x, numberOfKeys }
            : x
        ),
      ]);
  };

  const gameNumberOfKeysOnBlur = (game: BulkGameRow) => {
    setGames([
      ...games.map((x) =>
        x.gameId === game.gameId && !x.isSKU ? { ...x, numberOfKeys: '' } : x
      ),
    ]);
  };
  const [bulkTag, setBulkTag] = useState<string | undefined>();
  const bulkTagOnBlur = () => {
    setBulkTag(undefined);
  };
  const bulkChangeTag = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBulkTag(e.target.value);
    setGames([
      ...games.map((game) =>
        game.isSKU && filteredGames.some((x) => x.skuId === game.skuId)
          ? { ...game, tag: e.target.value }
          : game
      ),
    ]);
  };
  const changeTag = (tag: string, game: BulkGameRow) => {
    game.skuId
      ? setGames([
        ...games.map((x) => (x.skuId === game.skuId ? { ...x, tag } : x)),
      ])
      : setGames([
        ...games.map((x) =>
          x.gameId === game.gameId &&
            filteredGames.some((g) => g.skuId === x.skuId)
            ? { ...x, tag }
            : x
        ),
      ]);
  };
  const gameTagOnBlur = (game: BulkGameRow) => {
    setGames([
      ...games.map((x) =>
        x.gameId === game.gameId && !x.isSKU ? { ...x, tag: '' } : x
      ),
    ]);
  };

  useEffect(() => {
    setClasses('flex-grow-1');
  }, [setClasses]);

  const [availableGames, setAvailableGames] = useState<GameWithSkUs[] | undefined>();
  useEffect(() => {
    if (!availableGames && keyWorkflowId) {
      setIsLoading(true);
      getGamesForWorkflows(keyWorkflowId, '')
        .then((games) => {
          const g = games as unknown as GameWithSkUs[];
          const filtered = g.filter((x) => (x.skUs?.length ?? 0) > 0);
          setAvailableGames(filtered);
          setIsLoading(false);
        })
        .catch((e: Error) => {
          toast.error(e.message || t('Failed to get list of games'));
        });
    }
  }, [availableGames, keyWorkflowId, t]);

  const mapGame = (game: GameWithSkUs): BulkGameRow => ({
    gameId: game.id,
    name: game.name,
    isSKU: false,
    packshotUrl: game.packshotUrl,
    appId: game.appId,
    skuNames: game.skUs?.map((sku) => sku.name).filter(Boolean) as string[],
    packageIds: game.skUs?.map((sku) => sku.packageId).filter(Boolean) as string[],
    keyProviders: game.skUs?.map((sku) => sku.keyProviderId).filter(Boolean) as string[],
    platforms: game.skUs?.map((sku) => sku.platformId).filter(Boolean) as string[],
    territories: game.skUs
      ?.map((sku) =>
        sku.territories?.map((x) => territories?.find((y: { id: string }) => y.id === x))
      )
      .flat(),
  });

  const mapSKU = (sku: NonNullable<GameWithSkUs['skUs']>[number], game: GameWithSkUs) => ({
    skuId: sku.skuId,
    name: sku.name,
    isSKU: true,
    gameId: game.id,
    gameName: game.name,
    appId: game.appId,
    packageId: sku.packageId,
    packageType: sku.packageType,
    platformId: sku.platformId,
    platform: sku.platform,
    platformImageId: sku.platformImageId,
    platformColourHex: sku.platformColourHex,
    keyProviderId: sku.keyProviderId,
    keyProviderName: sku.keyProviderName,
    keyProviderImageId: sku.keyProviderImageId,
    keyProviderColourHex: sku.keyProviderColourHex,
    territoriesRestricted: sku.territoriesRestricted,
    territories: sku.territories?.map((x) =>
      territories?.find((y: { id: string }) => y.id === x)
    ),
  });
  const handleAddGame = (selected: GameWithSkUs[] | { skuId: string; skuName: string }[]) => {
    const list = selected as GameWithSkUs[];
    const game = list[0];
    if (!game || !game.skUs?.length) return;
    if (!games.some((x) => x.gameId === game.id)) {
      setGames([
        ...games,
        mapGame(game),
        ...game.skUs.map((sku) => mapSKU(sku, game)),
      ]);
    } else {
      const missingSKUs = game.skUs.filter(
        (sku) => !games.map((x) => x.skuId).includes(sku.skuId)
      );
      if (missingSKUs.length > 0) {
        const updatedGames: BulkGameRow[] = [];
        for (const sku of missingSKUs) {
          for (const existingGame of games) {
            if (existingGame.gameId !== game.id || !existingGame.isSKU) {
              updatedGames.push(existingGame);
            } else if ((sku.name ?? '').localeCompare(existingGame.name ?? '') === 1) {
              updatedGames.push(mapSKU(sku, game));
              updatedGames.push(existingGame);
            } else {
              updatedGames.push(existingGame);
              updatedGames.push(mapSKU(sku, game));
            }
          }
        }
        setGames(updatedGames);
      }
    }
  };

  const addBackCataglogue = () => {
    if (!availableGames) {
      toast.error(t('There are no games available'));
      return;
    }

    const gamesToAdd = availableGames.filter(
      (game: GameWithSkUs) => !games.some((x) => x.gameId === game.id)
    );
    setGames([
      ...games,
      ...gamesToAdd
        .map((game: GameWithSkUs) => [
          mapGame(game),
          ...(game.skUs ?? []).map((sku) => mapSKU(sku, game)),
        ])
        .flat(),
    ]);
  };

  const [filterGame, setFilterGame] = useState<string | undefined>();
  const handleFilterGame = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilterGame(e.target.value);
  };

  const [filterPackageType, setFilterPackageType] = useState<string | undefined>();
  const handleFilterPackageType = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilterPackageType(e.target.value);
  };

  const [filterAppPackageId, setFilterAppPackageId] = useState<string | undefined>();
  const handleFilterAppPackageId = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilterAppPackageId(e.target.value);
  };

  const [filterKeyProvider, setFilterKeyProvider] = useState<string | undefined>();
  const handleFilterKeyProvider = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilterKeyProvider(e.target.value);
  };

  const [filterPlatform, setFilterPlatform] = useState<string | undefined>();
  const handleFilterPlatform = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilterPlatform(e.target.value);
  };

  const [filterTerritory, setFilterTerritory] = useState<string | undefined>();
  const handleFilterTerritory = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilterTerritory(e.target.value);
  };

  const [filteredGames, setFilteredGames] = useState<BulkGameRow[]>([]);

  useEffect(() => {
    if (games) {
      const loweredGameName = filterGame?.toLowerCase() ?? '';
      const loweredPackageAppType = filterPackageType?.toLowerCase() ?? '';
      const loweredPackageAppId = filterAppPackageId?.toLowerCase() ?? '';

      const matchSKUGameName = (game: BulkGameRow) =>
        (game.gameName ?? '').toLowerCase().indexOf(loweredGameName) !== -1 ||
        (game.name ?? '').toLowerCase().indexOf(loweredGameName) !== -1;

      const matchSKUAppPackageType = (game: BulkGameRow) =>
        (game.packageType ?? '').toLowerCase().indexOf(loweredPackageAppType) !== -1;

      const matchSKUAppPackageId = (game: BulkGameRow) =>
        (game.packageId ?? '').toLowerCase().indexOf(loweredPackageAppId) !== -1 ||
        (game.appId ?? '').toLowerCase().indexOf(loweredPackageAppId) !== -1;

      const matchSKUKeyProvider = (game: BulkGameRow) =>
        !filterKeyProvider || game.keyProviderId === filterKeyProvider;

      const matchSKUPlatform = (game: BulkGameRow) =>
        !filterPlatform || game.platformId === filterPlatform;

      const matchSKUTerritoryId = (game: BulkGameRow) =>
        !filterTerritory ||
        game.territories?.some((x) => x?.id === filterTerritory);

      const matchGameName = (game: BulkGameRow) =>
        (game.name ?? '').toLowerCase().indexOf(loweredGameName) !== -1 ||
        game.skuNames?.some(
          (skuName: string) => skuName?.toLowerCase().indexOf(loweredGameName) !== -1
        );
      const matchGameAppId = (game: BulkGameRow) =>
        game.packageIds?.some(
          (packageId: string) =>
            packageId?.toLowerCase().indexOf(loweredPackageAppId) !== -1
        ) || (game.appId ?? '').toLowerCase().indexOf(loweredPackageAppId) !== -1;

      const matchGameKeyProvider = (game: BulkGameRow) =>
        !filterKeyProvider ||
        game.keyProviders?.some(
          (keyProvider: string) => keyProvider === filterKeyProvider
        );
      const matchGamePlatform = (game: BulkGameRow) =>
        !filterPlatform ||
        game.platforms?.some((platform: string) => platform === filterPlatform);

      const matchGameTerritoryId = (game: BulkGameRow) =>
        !filterTerritory ||
        game.territories?.some((x) => x?.id === filterTerritory);

      setFilteredGames([
        ...games.filter(
          (game) =>
            (game.isSKU &&
              matchSKUGameName(game) &&
              matchSKUTerritoryId(game) &&
              matchSKUAppPackageType(game) &&
              matchSKUAppPackageId(game) &&
              matchSKUKeyProvider(game) &&
              matchSKUPlatform(game)) ||
            (!game.isSKU &&
              matchGameName(game) &&
              matchGameTerritoryId(game) &&
              matchGameAppId(game) &&
              matchGameKeyProvider(game) &&
              matchGamePlatform(game))
        ),
      ]);
    }
  }, [
    filterGame,
    games,
    filterPackageType,
    filterAppPackageId,
    filterKeyProvider,
    filterPlatform,
    filterTerritory,
  ]);

  // For displaying the total count of SKUs
  const totalSKUsCount = games.filter((x) => x.isSKU).length;

  // For displaying the total count of games that are not SKUs
  const totalNonSKUGamesCount = games.filter((x) => !x.isSKU).length;

  // For displaying the count of games that are not SKUs and have associated SKUs with keys
  const nonSKUGamesWithKeysCount = games.filter(
    (x) =>
      !x.isSKU &&
      games.filter(
        (sku) =>
          sku.isSKU && sku.gameId === x.gameId && parseInt(String(sku.numberOfKeys ?? 0), 10) > 0
      ).length > 0
  ).length;

  // For displaying the count of SKUs with more than 0 keys
  const skusWithKeysCount = games.filter(
    (x) => x.isSKU && parseInt(String(x.numberOfKeys ?? 0), 10) > 0
  ).length;

  // For displaying the total number of keys across all SKUs
  const totalKeysSum = games
    .filter((x) => x.skuId && x.numberOfKeys)
    .map((x) => x.numberOfKeys)
    .reduce((a, b) => parseInt(String(a), 10) + parseInt(String(b), 10), 0);
  const totalKeysCount = CommaNumber_NoDecimal(String(totalKeysSum), '');

  const handleComplete = async () => {
    const skusWithKeys = games
      .filter((x) => x.isSKU && parseInt(String(x.numberOfKeys ?? 0), 10) > 0)
      .map((sku) => ({
        gameSKUId: String(sku.skuId ?? ''),
        requesterTagId: keyWorkflow?.tagsAsFreeText ? null : (sku.tag ?? undefined),
        requesterTag: keyWorkflow?.tagsAsFreeText ? (sku.tag ?? '') : '',
        numberOfKeys: parseInt(String(sku.numberOfKeys ?? 0), 10),
      }));
    setIsLoading(true);
    dispatch(
      bulkSaveRequests({ companyId: companyId!, keyWorkflowId: keyWorkflowId!, items: skusWithKeys })
    ).unwrap()
      .then(() => {
        toast.success(t('Request added to basket'));
        setCompleteRequestSlideoutOpen(true);
        setGames([]);
      })
      .catch((e) =>
        toast.error(e.message ? e.message : t('Failed to add request'))
      )
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (!keyProviders) {
      getKeyProviders().catch(() => { });
    }
  }, [keyProviders, getKeyProviders]);

  useEffect(() => {
    if (!platforms) {
      getPlatforms().catch(() => { });
    }
  }, [platforms, getPlatforms]);

  useEffect(() => {
    if (!territories) {
      getAllTerritories().catch(() => { });
    }
  }, [territories, getAllTerritories]);

  return !isLoading ? (
    <>
      <ActionBar breadcrumb={breadcrumb}>
        {games && games.length > 0 && (
          <Button
            className={`btn ${showRemove ? 'btn-primary' : 'btn-default btn-outline'
              } me-2`}
            onClick={() => toggleShowRemove(undefined)}
            text={showRemove ? t('Continue') : t('Remove items')}
          />
        )}
      </ActionBar>
      {!companyId && currentUser && (
        <SelectCompanyStep
          stepName="company"
          currentUser={currentUser}
          companies={companies}
          handleCompanySelect={(value: string) => setCompanyId(value)}
        ></SelectCompanyStep>
      )}

      {companyId && !keyWorkflowId && (
        <SelectKeyWorkflowStep
          stepName="keyWorkflow"
          keyWorkflows={keyWorkflows}
          handleKeyWorkflowSelect={(value: string) => setKeyWorkflowId(value)}
        ></SelectKeyWorkflowStep>
      )}

      {companyId && keyWorkflowId && (
        <>
          <KwPanel className="mb-4">
            <div className="d-flex">
              <div className="d-flex flex-column flex-grow-1 pe-4">
                <span className="mb-2">{t('Find a game')}</span>
                <div className="search-wrapper in-panel">
                  <Search
                    games={availableGames}
                    onSelect={(value: import('../../types/games').GameSkuBase[]) => handleAddGame(value as unknown as GameWithSkUs[])}
                    disabled={showRemove}
                  ></Search>

                  <div className="search-icon">
                    <FontAwesomeIcon icon={icons.SEARCH} />
                  </div>
                </div>
              </div>
              <div className="d-flex flex-column border-left ps-4">
                <span className="mb-2">{t('...or')}</span>
                <div>
                  <Button
                    className="btn btn-default px-50"
                    isDisabled={showRemove}
                    onClick={addBackCataglogue}
                    text={t('Browse all games')}
                  />
                </div>
              </div>
            </div>
          </KwPanel>
          {games.length > 0 && (
            <>
              <div className="grid-wrapper mb-4 bulk-requests promo-grid">
                <AutoSizer>
                  {({ width, height }: { width: number; height: number }) => (
                    <MultiGrid
                      fixedRowCount={1}
                      rowRenderer={rowRenderer}
                      cellRenderer={({
                        columnIndex,
                        key,
                        parent,
                        rowIndex,
                        style,
                      }: {
                        columnIndex: number;
                        key: string;
                        parent: unknown;
                        rowIndex: number;
                        style: React.CSSProperties;
                      }) =>
                        cellRenderer({
                          t,
                          columnIndex,
                          rowIndex,
                          games: filteredGames,
                          style,
                          key,
                          handleRemove,
                          bulkChangeNumberOfKeys,
                          bulkNumberOfKeys,
                          bulkNumberOfKeysOnBlur,
                          gameNumberOfKeysOnBlur,
                          changeNumberOfKeys,
                          bulkChangeTag,
                          bulkTag,
                          bulkTagOnBlur,
                          changeTag,
                          gameTagOnBlur,
                          keyWorkflow,
                          filterGame,
                          handleFilterGame,
                          filterPackageType,
                          handleFilterPackageType,
                          filterAppPackageId,
                          handleFilterAppPackageId,
                          filterKeyProvider,
                          handleFilterKeyProvider,
                          filterPlatform,
                          handleFilterPlatform,
                          platforms,
                          filterTerritory,
                          handleFilterTerritory,
                          territories,
                          keyProviders,
                        })
                      }
                      columnWidth={({ index }: { index: number }) =>
                        getColumnWidth({ index, keyWorkflow })
                      }
                      columnCount={8}
                      enableFixedColumnScroll
                      enableFixedRowScroll
                      fixedColumnCount={1}
                      height={height}
                      rowHeight={getRowHeight}
                      rowCount={filteredGames.length + 1}
                      width={width}
                      hideTopRightGridScrollbar
                      hideBottomLeftGridScrollbar
                    />
                  )}
                </AutoSizer>
              </div>

              <KwPanel className="d-flex mb-4 justify-content-between align-items-center">
                <div className="totals d-flex flex-grow-1">
                  <div className="d-flex flex-column  border-right flex-basis-584">
                    <span className="me-5">
                      <span className="d-inline-block w-60">{t('Games')}:</span>
                      <span>
                        {t(
                          '{nonSKUGamesWithKeysCount} of {totalNonSKUGamesCount}',
                          {
                            nonSKUGamesWithKeysCount: nonSKUGamesWithKeysCount,
                            totalNonSKUGamesCount: totalNonSKUGamesCount,
                          }
                        )}
                      </span>
                    </span>
                    <span className="me-5">
                      <span className="d-inline-block w-60">{t('Skus')}</span>
                      <span>
                        {t('{skusWithKeysCount} of {totalSKUsCount}', {
                          skusWithKeysCount: skusWithKeysCount,
                          totalSKUsCount: totalSKUsCount,
                        })}
                      </span>
                    </span>
                  </div>
                  <div className="ms-4 d-flex flex-column flex-grow-1 border-right">
                    <span>
                      <span className="d-inline-block w-60">{t('Keys')}:</span>
                      <span className="me-5">
                        {t('{totalKeysCount}', {
                          totalKeysCount: totalKeysCount,
                        })}
                      </span>
                    </span>
                    <span style={{ display: 'none' }}>
                      <span className="d-inline-block w-60">{t('Value')}:</span>
                      <span>0</span>
                    </span>
                  </div>
                </div>
                <Button
                  className="btn btn-primary btn-large ms-4"
                  isDisabled={skusWithKeysCount === 0 || showRemove}
                  onClick={handleComplete}
                  text={t('Complete')}
                />
              </KwPanel>
            </>
          )}
        </>
      )}
    </>
  ) : (
    <Loading></Loading>
  );
}
export default Bulk;
